import { useState } from 'react';
import axios from 'axios';

type PaymentIntentData = {
  amount: number; // Amount in cents
  currency: string;
  customer: string;
  payment_method: string;
  off_session?: boolean;
  confirm?: boolean;
  description?: string;
};

type StripePaymentIntent = {
  id: string;
  object: 'payment_intent';
  amount: number;
  currency: string;
  status: 'requires_payment_method' | 'requires_confirmation' | 'requires_action' | 'processing' | 'requires_capture' | 'canceled' | 'succeeded';
  customer: string;
  payment_method: string;
  description: string | null;
  created: number;
  client_secret: string;
};

type UsePaymentIntentReturn = {
  createPaymentIntent: (paymentIntentData: PaymentIntentData) => Promise<StripePaymentIntent | null>;
  loading: boolean;
  error: string | null;
  paymentIntent: StripePaymentIntent | null;
};

 const usePaymentIntent = (): UsePaymentIntentReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentIntent, setPaymentIntent] = useState<StripePaymentIntent | null>(null);

  const createPaymentIntent = async (paymentIntentData: PaymentIntentData): Promise<StripePaymentIntent | null> => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        'https://api.stripe.com/v1/payment_intents',
        new URLSearchParams({
          amount: paymentIntentData?.amount.toString(),
          currency: paymentIntentData?.currency,
          customer: paymentIntentData?.customer,
          payment_method: paymentIntentData?.payment_method,
          off_session: paymentIntentData?.off_session?.toString() || 'false',
          confirm: paymentIntentData?.confirm?.toString() || 'false',
          ...(paymentIntentData?.description && { description: paymentIntentData.description }),
        }),
        {
          headers: {
            'Authorization': `Bearer ${process.env.REACT_APP_STRIPE_SECRET_KEY}`,
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }
      );

      const newPaymentIntent: StripePaymentIntent = response.data;
      setPaymentIntent(newPaymentIntent);
      return newPaymentIntent;
    } catch (err) {
      const errorMessage = axios.isAxiosError(err) 
        ? err.response?.data?.error?.message || err.message 
        : 'An unknown error occurred';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    createPaymentIntent,
    loading,
    error,
    paymentIntent,
  };
};


export default usePaymentIntent;